import { useEffect, useMemo, useState, type ReactNode, type RefObject } from 'react';

import { CameraStreamFrameControlsContext } from './cameraStreamFrameControlsProvider/CameraStreamFrameControlsContext.ts';
import { useFrameRadialControlsVisibility } from './cameraStreamFrameControlsProvider/useFrameRadialControlsVisibility.ts';

type CameraStreamFrameControlsProviderProps = {
  scopeRef: RefObject<HTMLElement | null>;
  children: ReactNode;
};

/** Single pointer-idle visibility source for overlay controls inside the video frame. */
export const CameraStreamFrameControlsProvider = ({
  scopeRef,
  children,
}: CameraStreamFrameControlsProviderProps) => {
  const [pauseHideTimer, setPauseHideTimer] = useState(false);
  const frameControlsVisible = useFrameRadialControlsVisibility({
    scopeRef,
    pauseTimer: pauseHideTimer,
  });

  // Hold actions can end on blur/hidden tab without a pad pointer up (see useHoldAction).
  useEffect(() => {
    if (!frameControlsVisible) {
      setPauseHideTimer(false);
      return;
    }

    const resumeHideTimer = () => setPauseHideTimer(false);
    const onVisibilityChange = () => {
      if (document.hidden) resumeHideTimer();
    };

    window.addEventListener('blur', resumeHideTimer);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('blur', resumeHideTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [frameControlsVisible]);

  const value = useMemo(
    () => ({ frameControlsVisible, setPauseHideTimer }),
    [frameControlsVisible]
  );

  return (
    <CameraStreamFrameControlsContext.Provider value={value}>
      {children}
    </CameraStreamFrameControlsContext.Provider>
  );
};
